import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { SessionDetailDto } from "~/lib/api";

// Fallback when `<SessionProvider>` is mounted without an owning state (the
// pattern in screen tests that just need a static session). Real callers pass
// SessionLayout's setter so the optimistic lock lands; this is the safe-noop
// alternative rather than a lint-baiting empty arrow.
function noopMarkSessionLocked(): void {
  // intentionally empty
}

interface SessionContextValue {
  /** The handover session for the current `/session/:id` subtree. */
  session: SessionDetailDto;
  /**
   * Flip the session to `locked` locally without a refetch. Called from the
   * detail screen when the acknowledge endpoint reports `sessionLocked: true`
   * (HAN-57) so the lock banner + hidden edit affordances land in the same
   * frame as the toast. A no-op in tests that mount `<SessionProvider>`
   * directly without an owning state.
   */
  markSessionLocked: () => void;
}

// Loaded once by <SessionLayout> and provided to every nested session route, so
// patient / code / handover screens read the session (and its ward) from
// context instead of refetching. null until a provider is mounted.
const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  session,
  markSessionLocked,
  children,
}: {
  session: SessionDetailDto;
  markSessionLocked?: () => void;
  children: ReactNode;
}): JSX.Element {
  const value = useMemo<SessionContextValue>(
    () => ({ session, markSessionLocked: markSessionLocked ?? noopMarkSessionLocked }),
    [session, markSessionLocked],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Read the current session. Throws if used outside a `/session/:id` route. */
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within a <SessionLayout> (a /session/:id route)");
  }
  return ctx;
}
